// src/features/courses/hooks/useSearchCourses.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { getAuthClient } from '../../../auth/authClient';
import { AUTH_ENABLED } from '../../../auth/flags';
import { CONFIG } from '../../../config';

/**
 * SSE-based course search hook.
 * Streams results from /api/v1/search/courses as they arrive.
 *
 * @param {string} query - Search query string
 * @param {object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay (default: 300ms)
 * @param {number} options.limit - Max results to fetch (default: 10)
 * @returns {{ results: Array, isSearching: boolean, error: string|null, totalResults: number|null }}
 */
export function useSearchCourses(query, { debounceMs = 300, limit = 10 } = {}) {
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [totalResults, setTotalResults] = useState(null);
  const abortControllerRef = useRef(null);

  const performSearch = useCallback(async (searchQuery) => {
    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsSearching(true);
    setError(null);
    setResults([]);
    setTotalResults(null);

    try {
      // Build URL
      const base = CONFIG?.API_BASE_URL || window.location.origin;
      const url = new URL('/api/v1/search/courses', base);
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('limit', String(limit));

      // Get auth token if available
      const headers = {
        'Accept': 'text/event-stream',
      };

      if (AUTH_ENABLED) {
        try {
          const auth = getAuthClient();
          const idToken = await auth.getIdToken();
          if (idToken) {
            headers['Authorization'] = `Bearer ${idToken}`;
          }
        } catch (e) {
          console.warn('[useSearchCourses] Auth token fetch failed:', e);
        }
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let buffer = '';
      let currentEvent = '';
      let currentData = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            currentData = line.slice(5).trim();
          } else if (line === '' && currentEvent && currentData) {
            // Empty line = end of event
            handleEvent(currentEvent, currentData);
            currentEvent = '';
            currentData = '';
          }
        }
      }

      // Handle any remaining buffered event
      if (currentEvent && currentData) {
        handleEvent(currentEvent, currentData);
      }

    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Search failed');
      }
    } finally {
      setIsSearching(false);
    }

    function handleEvent(event, data) {
      try {
        const parsed = JSON.parse(data);

        switch (event) {
          case 'result':
            setResults((prev) => [...prev, parsed]);
            break;
          case 'complete':
            if (typeof parsed.totalResults === 'number') {
              setTotalResults(parsed.totalResults);
            }
            break;
          case 'error':
            setError(parsed.message || 'Search error');
            break;
          default:
            // Ignore unknown event types
            break;
        }
      } catch (e) {
        console.error('[useSearchCourses] Failed to parse SSE data:', e);
      }
    }
  }, [limit]);

  // Debounced search effect
  useEffect(() => {
    // Clear results if query is empty
    if (!query || !query.trim()) {
      setResults([]);
      setIsSearching(false);
      setError(null);
      setTotalResults(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(query.trim());
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [query, debounceMs, performSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Manual clear function
  const clearSearch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setResults([]);
    setIsSearching(false);
    setError(null);
    setTotalResults(null);
  }, []);

  return {
    results,
    isSearching,
    error,
    totalResults,
    clearSearch,
  };
}
